// src/payment/adapters/StripeAdapter.js
import axios from 'axios';
import API from '../../api/config/api.config';
import ENV from '../../api/config/env.config';
import { loadStripe } from '@stripe/stripe-js';

export class StripeAdapter {
  constructor(slug, resumen) {
    this.slug = slug;
    this.resumen = resumen;
    this.transactionId = Date.now();
    this.stripe = null;
    this.publishableKey = null;
    this._initialized = false;
  }

  async initialize() {
    if (this._initialized && this.stripe) {
      return this.stripe;
    }

    try {
      const config = await this._getConfig();
      this.publishableKey = config.publishableKey;
      this.stripe = await loadStripe(this.publishableKey);
      this._initialized = true;
      return this.stripe;
    } catch (error) {
      console.error('[StripeAdapter] Error inicializando:', error);
      throw error;
    }
  }

  async createPaymentIntent() {
    try {
      if (!this._initialized) {
        await this.initialize();
      }

      // Se envía la estructura limpia tal cual para que el backend 
      // resuelva precios y la conversión a USD con la tasa oficial.
      const payload = {
        eventSlug: this.slug,
        datos_inscripcion_guardado: {
          ...this.resumen
        },
        metadata: {
          transactionId: this.transactionId,
          package: this.resumen.paqueteSeleccionado?.title || 'sin-paquete',
          id_package: this.resumen.paqueteSeleccionado?.id_package || this.resumen.paqueteSeleccionado?.id || '',
        },
      };

     

      const response = await axios.post(
        `${ENV.API.BASE_URL}${API.STRIPE.PAYMENT_INTENT}`,
        payload,
        { headers: this._getHeaders() }
      );

      const clientSecret = response.data?.clientSecret;

      if (!clientSecret) {
        throw new Error('Stripe no devolvió clientSecret');
      }

      return clientSecret;
    } catch (error) {
      console.error('[StripeAdapter] Error creando payment intent:', error);
      throw error;
    }
  }

  async createAndConfirmPayment(cardElement, existingStripeInstance) {
    try {
      if (existingStripeInstance) {
        this.stripe = existingStripeInstance;
        this._initialized = true;
      } else {
        await this.initialize();
      }

      const clientSecret = await this.createPaymentIntent();

      if (!clientSecret) {
        throw new Error('No se pudo crear el Payment Intent');
      }

      return await this.confirmPayment(clientSecret, cardElement);
    } catch (error) {
      console.error('[StripeAdapter] Error en flujo completo:', error);
      throw error;
    }
  }

  async confirmPayment(clientSecret, cardElement) {
    if (!this.stripe) {
      throw new Error('Stripe no inicializado. Llama a initialize() primero.');
    }

    if (!cardElement) {
      throw new Error('CardElement no proporcionado');
    }

    const { error, paymentIntent } = await this.stripe.confirmCardPayment(clientSecret, {
      payment_method: {
        card: cardElement,
        billing_details: {
          name: this._getNombreCompleto(),
          email: this._getEmail(),
        },
      },
    });

    if (error) {
      console.error('[StripeAdapter] Error confirmando pago:', error);
      throw error;
    }

    return paymentIntent;
  }

  getStripeInstance() {
    return this.stripe;
  }

  async _getConfig() {
    const response = await axios.get(
      `${ENV.API.BASE_URL}${API.STRIPE.CONFIG}/${this.slug}`,
    { headers: this._getHeaders() }
    );
    
    const publishableKey = response.data?.publishable_key;
    
    if (!publishableKey) {
      throw new Error(
        response.data?.message || 'No se encontró clave pública de Stripe'
      );
    }
    
    return { publishableKey };
    
    }

  _getNombreCompleto() {
    const campos = this.resumen?.camposActivos || [];
    const nombre = campos.find(c => c.nombre?.toLowerCase().includes('nombre'))?.valor || '';
    const apellido = campos.find(c => c.nombre?.toLowerCase().includes('apellido'))?.valor || '';
    return apellido ? `${nombre} ${apellido}` : nombre;
  }

  _getEmail() {
    const campos = this.resumen?.camposActivos || [];
    return campos.find(c => c.nombre?.toLowerCase().includes('email'))?.valor || '';
  }

  _getHeaders() {
    const token = localStorage.getItem('token');
    return {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      'Content-Type': 'application/json',
    };
  }
}